import React, { Component } from "react";
import { withTranslation, WithTranslation } from "react-i18next";
import Column from "@amzn/meridian/column";
import Row from "@amzn/meridian/row";
import Box from "@amzn/meridian/box";
import Expander from "@amzn/meridian/expander";
import Button from "@amzn/meridian/button";
import Alert from "@amzn/meridian/alert";

import rumService from "@/utils/awsRUM/awsRUM";
import { copyToClipboard } from "@/utils/clipboardUtils";
import {
  buildDiagnosticPayload,
  serializeDiagnosticPayload,
  safeStringify,
  type DiagnosticPayload,
} from "@/app/routes/components/diagnosticSerializer";
import { keys } from "@/i18n/keys";

interface OwnProps {
  children: React.ReactNode;
}

type Props = OwnProps & WithTranslation;

interface State {
  hasError: boolean;
  isChunkError: boolean;
  /** Number of silent retries used for runtime errors in this boundary instance. */
  runtimeRetryCount: number;
  /** Diagnostic payload captured from the most recent error, for display in the fallback UI. */
  diagnosticPayload: DiagnosticPayload | null;
  /** Controlled state for the Meridian diagnostics disclosure. */
  isDiagnosticsOpen: boolean;
}

/** Max auto-retries for chunk load errors before showing the error UI. */
const MAX_CHUNK_RETRIES = 2;
const CHUNK_RETRY_KEY = "ytyard360-chunk-retry-count";

/**
 * Max silent re-mount attempts for runtime errors before surfacing the error
 * UI. Lets transient render errors (stale closures, one-off race conditions)
 * recover invisibly so users can keep working — only persistent errors
 * interrupt the page.
 *
 * Trade-off: a soft retry unmounts and re-mounts the route subtree, so route-
 * local component state (useState / useRef / scroll position) resets. Redux
 * state survives because the store sits outside the boundary.
 */
const MAX_RUNTIME_SILENT_RETRIES = 1;

/**
 * Extracts a message string from an unknown thrown value.
 * getDerivedStateFromError receives `unknown` despite React typing it as Error.
 */
function extractMessage(value: unknown): string {
  try {
    if (value instanceof Error) return value.message;
    if (typeof value === "string") return value;
    if (value === null || value === undefined) return "";
    if (typeof value === "object" && "message" in value) {
      const msg = (value as { message: unknown }).message;
      return typeof msg === "string" ? msg : safeStringify(msg);
    }
  } catch {
    // A thrown Proxy may reject instanceof, `in`, or property access.
  }
  return "";
}

/** Reads Error.name without trusting prototype/property traps on thrown values. */
function extractName(value: unknown): string {
  try {
    return value instanceof Error ? value.name : "";
  } catch {
    return "";
  }
}

/**
 * Converts an unknown thrown value to an Error | string suitable for
 * rumService.error. Never passes a plain object (which would emit
 * `[object Object]`).
 */
function toRumError(value: unknown): Error | string {
  if (typeof value === "string") return value;
  try {
    if (value instanceof Error) return value;
  } catch {
    // safeStringify supplies the stable fallback for hostile Proxies.
  }
  return safeStringify(value);
}

/**
 * Error boundary for lazy-loaded route chunks.
 *
 * Catches chunk load failures (network issues, deploy during session)
 * and shows a retry button instead of a blank screen.
 *
 * WHY class component: React error boundaries require componentDidCatch
 * which is only available in class components (as of React 18).
 * WHY withTranslation: Class components cannot use useTranslation hook.
 */
class RouteErrorBoundaryInner extends Component<Props, State> {
  /** Track CSS chunk errors we've already seen to prevent re-render loops. */
  private suppressedCssErrors = new Set<string>();

  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      isChunkError: false,
      runtimeRetryCount: 0,
      diagnosticPayload: null,
      isDiagnosticsOpen: false,
    };
  }

  static getDerivedStateFromError(error: unknown): Partial<State> {
    const message = extractMessage(error);

    // CSS chunk failures degrade gracefully (unstyled but functional).
    // Font/image/analytics failures don't reach error boundaries.
    // Only JS chunk failures and runtime errors are treated as fatal.
    const isCssChunkError = message.includes("Loading CSS chunk");
    if (isCssChunkError) {
      return { hasError: false, isChunkError: false };
    }

    const name = extractName(error);
    const isChunkError = name === "ChunkLoadError" || message.includes("Loading chunk");

    return { hasError: true, isChunkError };
  }

  componentDidCatch(error: unknown, errorInfo: React.ErrorInfo): void {
    const message = extractMessage(error);
    const name = extractName(error);
    const isCssChunkError = message.includes("Loading CSS chunk");

    if (isCssChunkError) {
      // Log but don't act — page renders unstyled but functional.
      // Track to prevent duplicate logging on re-render.
      if (!this.suppressedCssErrors.has(message)) {
        this.suppressedCssErrors.add(message);
        rumService.warn("CSS chunk load failed (non-fatal)", { error: message });
      }
      return;
    }

    const isJsChunkError =
      (name === "ChunkLoadError" || message.includes("Loading chunk")) &&
      !message.includes("Loading CSS chunk");

    if (isJsChunkError) {
      rumService.error("Route chunk load error", toRumError(error));
      // Auto-retry JS chunk errors (e.g., after deployment) before showing error UI.
      const retryCount = Number(sessionStorage.getItem(CHUNK_RETRY_KEY) || "0");
      if (retryCount < MAX_CHUNK_RETRIES) {
        sessionStorage.setItem(CHUNK_RETRY_KEY, String(retryCount + 1));
        window.location.reload();
        return;
      }
      sessionStorage.removeItem(CHUNK_RETRY_KEY);

      // Chunk retries exhausted — capture diagnostics for the fallback UI.
      const payload = buildDiagnosticPayload(
        error,
        errorInfo.componentStack ?? null,
        new Date().toISOString()
      );
      this.setState({ diagnosticPayload: payload });
      return;
    }

    // Silently re-mount the route subtree once for transient runtime errors —
    // a stale closure or one-off race usually clears itself after re-render.
    // If the error reappears, the budget is exhausted and the fallback UI
    // shows on the next throw. Gate condition lives inside the functional
    // updater so two errors in the same commit batch can't race past the cap.
    rumService.error("Route runtime error", toRumError(error), {
      silentRetry: this.state.runtimeRetryCount < MAX_RUNTIME_SILENT_RETRIES,
      retryCount: this.state.runtimeRetryCount,
    });

    const capturedAt = new Date().toISOString();
    this.setState((prev) =>
      prev.runtimeRetryCount < MAX_RUNTIME_SILENT_RETRIES
        ? {
            ...prev,
            // Silent retry — clear any stale diagnostics from a previous error cycle.
            hasError: false,
            isChunkError: false,
            runtimeRetryCount: prev.runtimeRetryCount + 1,
            diagnosticPayload: null,
            isDiagnosticsOpen: false,
          }
        : {
            ...prev,
            // Budget exhausted — retain the fallback and attach its diagnostics.
            diagnosticPayload: buildDiagnosticPayload(
              error,
              errorInfo.componentStack ?? null,
              capturedAt
            ),
          }
    );
  }

  handleRetry = (): void => {
    sessionStorage.removeItem(CHUNK_RETRY_KEY);
    this.setState({
      hasError: false,
      isChunkError: false,
      runtimeRetryCount: 0,
      diagnosticPayload: null,
      isDiagnosticsOpen: false,
    });
    window.location.reload();
  };

  handleDiagnosticsChange = (isDiagnosticsOpen: boolean): void => {
    this.setState({ isDiagnosticsOpen });
  };

  handleCopyLogs = (): void => {
    const { diagnosticPayload } = this.state;
    if (!diagnosticPayload) return;

    const logString = serializeDiagnosticPayload(diagnosticPayload);
    copyToClipboard(logString).catch((err: unknown) => {
      rumService.warn("Failed to copy error logs to clipboard", {
        error: safeStringify(err),
      });
    });
  };

  render(): React.ReactNode {
    const { t } = this.props;

    if (this.state.hasError) {
      const logString = this.state.diagnosticPayload
        ? serializeDiagnosticPayload(this.state.diagnosticPayload)
        : null;

      return (
        <Column
          height="100%"
          alignmentHorizontal="center"
          alignmentVertical="center"
          spacing="400"
          data-testid="route-error-boundary"
        >
          <Alert type="error" size="large" width="min(80vw, 720px)">
            <Column spacing="300" width="100%">
              <div>
                {this.state.isChunkError ? t(keys.ROUTE_ERROR_CHUNK) : t(keys.ROUTE_ERROR_GENERIC)}
              </div>
              {logString && (
                <Expander
                  type="inline"
                  title={t(keys.ROUTE_ERROR_REASON)}
                  open={this.state.isDiagnosticsOpen}
                  onChange={this.handleDiagnosticsChange}
                  alwaysRenderChildren
                  data-testid="error-diagnostics-details"
                >
                  <Box
                    type="outline"
                    spacingInset="400"
                    width="100%"
                    height="40vh"
                    minHeight={240}
                    maxHeight={420}
                    data-testid="error-diagnostics-panel"
                  >
                    <Column height="100%" heights={["fit", "fill"]} spacing="300">
                      <Row width="100%" alignmentHorizontal="end">
                        <Button
                          size="small"
                          type="tertiary"
                          onClick={this.handleCopyLogs}
                          data-testid="copy-error-logs-button"
                        >
                          {t(keys.ROUTE_ERROR_COPY_LOGS)}
                        </Button>
                      </Row>
                      <Box height="100%" overflowX="auto" overflowY="auto">
                        <pre
                          data-testid="error-diagnostics-content"
                          style={{
                            margin: 0,
                            fontSize: "12px",
                            lineHeight: "1.4",
                            whiteSpace: "pre-wrap",
                            wordBreak: "break-word",
                          }}
                        >
                          <code>{logString}</code>
                        </pre>
                      </Box>
                    </Column>
                  </Box>
                </Expander>
              )}
            </Column>
          </Alert>
          <Row>
            <Button type="primary" onClick={this.handleRetry}>
              {t(keys.RELOAD_PAGE)}
            </Button>
          </Row>
        </Column>
      );
    }

    return this.props.children;
  }
}

export const RouteErrorBoundary = withTranslation()(RouteErrorBoundaryInner);
